import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { SitterReviewService } from 'us-web-services';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';

const MAX_LEN = 1000;

export const ReviewDetail = observer((): JSX.Element | null => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: sitterIdParam, reviewId } = useParams<{ id: string; reviewId: string }>();
  const { stores } = useDependencies();
  const user = stores.user.user;
  const store = stores.sitterReview;

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = 'UrbanSitter - Review Caregiver';
  }, []);

  useEffect(() => {
    const sitterId = Number(sitterIdParam);
    const userId = user?.id;
    if (!userId || !sitterId) return;
    store.loadReasons();

    // If the store was cleared (e.g. direct URL hit or refresh) and this URL
    // carries a reviewId > 0, hydrate by fetching that review by filter.
    const reviewIdNum = Number(reviewId) || 0;
    if (!store.review && reviewIdNum > 0) {
      void (async (): Promise<void> => {
        try {
          const resp = await SitterReviewService.getByFilter({ params: { parentId: userId, userId, sitterId } });
          const existing = (resp.data?.data ?? [])[0];
          if (existing) {
            store.loadForSitter(sitterId, userId);
          }
        } catch (err) {
          console.warn('[SitterReviewDetail] hydrate failed', err);
        }
      })();
    }
  }, [sitterIdParam, reviewId, user?.id, store]);

  const review = store.review;
  const sitter = store.sitter ?? review?.sitter;
  const rating = review?.rating ?? 0;
  const isPositive = rating > 3;
  const comment = review?.comment ?? '';
  const remaining = MAX_LEN - comment.length - (comment.match(/\n/g) || []).length;

  const onSubmit = async (): Promise<void> => {
    if (!review || !sitter?.id) return;
    setSaving(true);
    try {
      await store.save();
      store.clear();
      navigate(`/sitter/${sitter.id}`);
    } catch (err) {
      console.warn('[SitterReviewDetail] submit failed', err);
      toast.error(t('pages.sitterReview.detail.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const onEditRating = (): void => {
    if (!sitter?.id) return;
    navigate(`/sitter/${sitter.id}/review`);
  };

  if (!review || !sitter) {
    return <div className='container py-6' />;
  }

  const heading = isPositive
    ? t('pages.sitterReview.detail.headingPositive')
    : t('pages.sitterReview.detail.headingNegative');
  const subHeading = isPositive
    ? t('pages.sitterReview.detail.subPositive')
    : t('pages.sitterReview.detail.subNegative');
  const hint = isPositive
    ? t('pages.sitterReview.detail.hintPositive')
    : t('pages.sitterReview.detail.hintNegative');

  const halfIdx = Math.ceil(store.reasons.length / 2);
  const leftReasons = store.reasons.slice(0, halfIdx);
  const rightReasons = store.reasons.slice(halfIdx);

  return (
    <div className='container py-6 max-w-2xl mx-auto'>
      <div className='bg-white rounded-lg border border-neutral-200 p-5'>
        <h1 className='text-2xl font-semibold'>{heading}</h1>
        <div className='flex items-start gap-3 mt-3'>
          <a href={`/app/sitter/${sitter.id}`}>
            <img
              src={sitter.pictureUrl}
              alt={sitter.firstName}
              className='w-20 h-20 rounded object-cover'
            />
          </a>
          <div>
            <div className='flex gap-0.5'>
              {[0, 1, 2, 3, 4].map((i) => (
                <span key={i} className={i < rating ? 'text-amber-400' : 'text-neutral-300'}>★</span>
              ))}
            </div>
            <p className='mt-1 text-sm'>
              {t('pages.sitterReview.detail.starsGiven', { name: sitter.firstName, count: rating })}
            </p>
          </div>
        </div>

        <hr className='my-4' />

        {!isPositive && (
          <>
            <h3 className='font-semibold'>
              {t('pages.sitterReview.detail.reasonsHeading', { name: sitter.firstName })}
            </h3>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2'>
              {[leftReasons, rightReasons].map((col, idx) => (
                <div key={idx} className='space-y-2'>
                  {col.map((reason) => (
                    <label key={reason.type} className='flex items-center gap-2 cursor-pointer'>
                      <input
                        type='checkbox'
                        checked={!!reason.selected}
                        onChange={(): void => store.toggleReason(reason.type)}
                      />
                      <span>{reason.label}</span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
            <hr className='my-4' />
          </>
        )}

        <h3 className='font-semibold'>
          {subHeading}
          {!isPositive && <em className='ml-1'>{t('pages.sitterReview.detail.required')}</em>}
        </h3>
        <p className='italic text-sm mt-1'>
          {hint}{' '}
          <a href='/review-guidelines' target='_blank' rel='noreferrer' className='underline'>
            {t('pages.sitterReview.detail.guidelinesLink')}
          </a>
        </p>
        <div className='mt-2'>
          <div className='text-xs text-right'>
            {t('pages.sitterReview.detail.charactersRemaining', { count: remaining })}
          </div>
          <textarea
            aria-label={t('pages.sitterReview.detail.textareaLabel')}
            className='w-full border border-neutral-300 rounded p-2 min-h-[140px]'
            rows={6}
            value={comment}
            maxLength={MAX_LEN}
            onChange={(e): void => store.setComment(e.target.value)}
            placeholder={t('pages.sitterReview.detail.placeholder')}
          />
        </div>

        <div className='mt-4'>
          {store.visibilityPublic ? (
            <p className='text-sm'>
              <strong>{t('pages.sitterReview.detail.publicLabel')}</strong>{' '}
              {t('pages.sitterReview.detail.publicDescription')}{' '}
              <Button buttonType={ButtonType.tertiary} className='underline' onClick={store.toggleVisibility}>
                {t('pages.sitterReview.detail.makePrivate')}
              </Button>
            </p>
          ) : (
            <p className='text-sm'>
              <strong>{t('pages.sitterReview.detail.privateLabel')}</strong>{' '}
              {t('pages.sitterReview.detail.privateDescription')}{' '}
              <Button buttonType={ButtonType.tertiary} className='underline' onClick={store.toggleVisibility}>
                {t('pages.sitterReview.detail.makePublic')}
              </Button>
            </p>
          )}
        </div>

        <div className='flex flex-col sm:flex-row gap-3 mt-5'>
          <Button
            buttonType={ButtonType.primary}
            onClick={onSubmit}
            disabled={saving || (!isPositive && !comment.trim())}
          >
            {t('pages.sitterReview.detail.submit')}
          </Button>
          <Button buttonType={ButtonType.secondary} onClick={onEditRating}>
            {t('pages.sitterReview.detail.editRating')}
          </Button>
        </div>
      </div>
    </div>
  );
});
